import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import React, { useEffect, useMemo } from "react";
import { Icon } from '@iconify/react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_INVOICES,
    SOCIETY_PORTAL_MEMBERS,
    SOCIETY_PORTAL_COMPLAINTS,
    SOCIETY_PORTAL_TASKS,
    SOCIETY_PORTAL_RECEIPTS_CREATE,
    SOCIETY_PORTAL_NOTICES,
    SOCIETY_PORTAL_NOTICES_ADD
} from "../../../Routing/routes";
import {
    getSocietyDashboard,
    getOperationsDashboard
} from "../../../redux/actions/SocietyPortal/DashboardAction/DashboardAction";
import { getFlatsWithOwnership } from "../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction";
import { getNoticeBoard } from "../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction";
import { selectedSocietyStorer } from "../../../utils/webStorer";
import OperationsSection from "../../../components/OperationsSection";

// Notice bodies are rich text; the card shows a plain-text excerpt.
const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();
    const {
        data: dashboardData,
        loading,
        operations: opsData,
        operationsLoading: opsLoading
    } = useSelector((s) => s.DashboardReducer || {});
    const { flatsWithOwnership = [] } = useSelector((s) => s.WingsAndFlatsReducers || {});
    const { noticeBoardData } = useSelector((s) => s.NoticeReducer || {});

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            dispatch(getSocietyDashboard({ societyId }));
            // Fallback source for occupancy/vacancy when the dashboard endpoint
            // doesn't yet aggregate them.
            dispatch(getFlatsWithOwnership({ society_id: societyId }));
            dispatch(getNoticeBoard({ societyId, page: 1, limit: 3 }));
            // Operations panel (complaints + tasks). Society context is
            // read from the session on the server; society_id from the FE
            // is ignored for non-super-admin sessions.
            dispatch(getOperationsDashboard({}));
        }
    }, [dispatch, societyId, refreshKey]);

    const apiStats = dashboardData?.stats || {};
    const apiTxns = dashboardData?.recent_transactions || [];
    const society = dashboardData?.society;

    // Derive occupancy from the flats list. Treat OWNED + RENTED as occupied,
    // VACANT as vacant. Other statuses (e.g. SOCIETY_OWNED, UNDER_RENOVATION)
    // still count toward total units but not toward either bucket.
    const flatStats = useMemo(() => {
        const flats = Array.isArray(flatsWithOwnership) ? flatsWithOwnership : [];
        const total = flats.length;
        const occupied = flats.filter(f => f.ownership_type === 'OWNED' || f.ownership_type === 'RENTED').length;
        const vacant = flats.filter(f => f.ownership_type === 'VACANT').length;
        const rate = total > 0 ? Math.round((occupied / total) * 100) : 0;
        return { total, occupied, vacant, rate };
    }, [flatsWithOwnership]);

    // For each occupancy metric, prefer the backend value when it has data;
    // otherwise fall back to what we computed from the flats list.
    const pickStat = (apiVal, fallback) => (Number(apiVal) > 0 ? Number(apiVal) : fallback);

    const stats = useMemo(() => ({
        totalUnits:     pickStat(apiStats.total_units,     flatStats.total),
        occupiedUnits:  pickStat(apiStats.occupied_units,  flatStats.occupied),
        vacantUnits:    pickStat(apiStats.vacant_units,    flatStats.vacant),
        occupancyRate:  pickStat(apiStats.occupancy_rate,  flatStats.rate),
        totalMembers: apiStats.total_members ?? 0,
        ownersCount: apiStats.owners_count ?? 0,
        tenantsCount: apiStats.tenants_count ?? 0,
        membersAddedThisMonth: apiStats.members_added_this_month ?? 0,
        pendingDues: apiStats.pending_dues ?? 0,
        collectedThisMonth: apiStats.collected_this_month ?? 0,
        collectionRate: apiStats.collection_rate ?? 0,
        pendingComplaints: apiStats.pending_complaints ?? 0,
        newNotices: apiStats.new_notices ?? 0
    }), [apiStats, flatStats]);

    const recentTransactions = useMemo(() => apiTxns.map((t) => ({
        id: t.id,
        receiptNumber: t.receipt_number || '',
        unit: t.unit || '-',
        member: t.member || '-',
        amount: Number(t.amount) || 0,
        type: t.type === 'ADVANCE' ? 'Advance' : 'Payment',
        paymentMode: t.payment_mode || '',
        date: t.date,
        status: t.status === 'CLEARED' ? 'Paid' : (t.status === 'PENDING' ? 'Pending' : t.status)
    })), [apiTxns]);

    // The society's own published notices. These three cards used to hold
    // invented notices (and an Upcoming Events card for a module that does not
    // exist), which read as dummy data on a live dashboard.
    const notices = useMemo(() => {
        const rows = Array.isArray(noticeBoardData) ? noticeBoardData : (noticeBoardData?.rows || []);
        return rows.slice(0, 3).map((n) => ({
            id: n.notice_id || n.id,
            title: n.title,
            description: stripHtml(n.summary || n.content || n.body || ''),
            date: n.published_at || n.publish_at || n.created_at,
            priority: String(n.priority || 'NORMAL').toLowerCase()
        }));
    }, [noticeBoardData]);

    const quickActions = [
        { title: 'Create Invoice', icon: 'mdi:file-document-plus-outline', link: SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES, gradient: 'tw-from-blue-500 tw-to-blue-600' },
        { title: 'Add Member', icon: 'mdi:account-plus-outline', link: SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS, gradient: 'tw-from-emerald-500 tw-to-emerald-600' },
        { title: 'Send Notice', icon: 'mdi:bullhorn-outline', link: SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_ADD, gradient: 'tw-from-amber-500 tw-to-amber-600' },
        { title: 'Record Payment', icon: 'mdi:cash-plus', link: SOCIETY_PORTAL + SOCIETY_PORTAL_RECEIPTS_CREATE, gradient: 'tw-from-purple-500 tw-to-purple-600' },
    ];

    const formatCurrency = (amount) => `₹${(Number(amount) || 0).toLocaleString('en-IN')}`;
    const formatDate = (dateStr) => dateStr
        ? new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
        : '-';

    // Notice priorities are LOW / NORMAL / HIGH / URGENT.
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'tw-bg-red-100 tw-text-red-700 tw-border-red-200';
            case 'high': return 'tw-bg-orange-100 tw-text-orange-700 tw-border-orange-200';
            case 'low': return 'tw-bg-emerald-100 tw-text-emerald-700 tw-border-emerald-200';
            default: return 'tw-bg-blue-100 tw-text-blue-700 tw-border-blue-200';
        }
    };


    return (
        <>
            {/* Welcome Banner */}
            <div className="tw-bg-gradient-to-r tw-from-blue-600 tw-via-blue-700 tw-to-indigo-700 tw-rounded-xl tw-p-5 tw-mb-5 tw-relative tw-overflow-hidden">
                <div className="tw-absolute tw-right-0 tw-top-0 tw-w-64 tw-h-full tw-opacity-10">
                    <Icon icon="mdi:home-city-outline" className="tw-w-full tw-h-full tw-text-white" />
                </div>
                <div className="tw-relative tw-z-10">
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <div>
                            <h1 className="tw-text-xl tw-font-bold tw-text-white tw-mb-1">
                                {loading && !society ? 'Loading…' : `Welcome to ${society?.name || 'your society'}`}
                            </h1>
                            <p className="tw-text-blue-100 tw-text-sm">Here's what's happening in your society today</p>
                        </div>
                        <div className="tw-text-right">
                            <p className="tw-text-blue-100 tw-text-xs">Today</p>
                            <p className="tw-text-white tw-font-semibold">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Operations Overview — consolidated complaints + tasks */}
            <div className="tw-mb-5">
                <OperationsSection
                    data={opsData}
                    loading={opsLoading}
                    onOpenComplaints={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS)}
                    onOpenTasks={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_TASKS)}
                />
            </div>

            {/* Quick Stats */}
            <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mb-5">
                {/* Financial Overview Card */}
                <div className="tw-col-span-2 tw-bg-gradient-to-br tw-from-emerald-500 tw-to-teal-600 tw-rounded-xl tw-p-4 tw-text-white tw-relative tw-overflow-hidden">
                    <div className="tw-absolute tw-right-2 tw-bottom-2 tw-opacity-10">
                        <Icon icon="mdi:cash-multiple" className="tw-text-[80px]" />
                    </div>
                    <div className="tw-relative tw-z-10">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                            <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-white/20 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:chart-line" className="tw-text-xl" />
                            </div>
                            <span className="tw-text-emerald-100 tw-text-sm tw-font-medium">Financial Overview</span>
                        </div>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                            <div>
                                <p className="tw-text-emerald-100 tw-text-xs tw-mb-0.5">Collected This Month</p>
                                <p className="tw-text-2xl tw-font-bold">{formatCurrency(stats.collectedThisMonth)}</p>
                            </div>
                            <div>
                                <p className="tw-text-emerald-100 tw-text-xs tw-mb-0.5">Pending Dues</p>
                                <p className="tw-text-2xl tw-font-bold">{formatCurrency(stats.pendingDues)}</p>
                            </div>
                        </div>
                        <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-white/20">
                            <div className="tw-flex tw-justify-between tw-text-xs">
                                <span className="tw-text-emerald-100">Collection Rate</span>
                                <span className="tw-font-medium">{stats.collectionRate}%</span>
                            </div>
                            <div className="tw-w-full tw-bg-white/20 tw-rounded-full tw-h-1.5 tw-mt-1">
                                <div className="tw-bg-white tw-h-1.5 tw-rounded-full" style={{ width: `${stats.collectionRate}%` }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Units Card */}
                <div className="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
                        <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:home-group" className="tw-text-white tw-text-xl" />
                        </div>
                        <span className="tw-text-xs tw-text-emerald-600 tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium">
                            {stats.occupancyRate}% Occupied
                        </span>
                    </div>
                    <p className="tw-text-2xl tw-font-bold tw-text-gray-800">{stats.occupiedUnits}/{stats.totalUnits}</p>
                    <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Total Units</p>
                    <div className="tw-flex tw-gap-2 tw-mt-3">
                        <span className="tw-text-[10px] tw-bg-blue-50 tw-text-blue-600 tw-px-2 tw-py-0.5 tw-rounded">
                            <Icon icon="mdi:check-circle" className="tw-inline tw-mr-0.5" />{stats.occupiedUnits} Occupied
                        </span>
                        <span className="tw-text-[10px] tw-bg-gray-50 tw-text-gray-600 tw-px-2 tw-py-0.5 tw-rounded">
                            <Icon icon="mdi:home-outline" className="tw-inline tw-mr-0.5" />{stats.vacantUnits} Vacant
                        </span>
                    </div>
                </div>

                {/* Members Card */}
                <div className="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
                        <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-bg-purple-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:account-group" className="tw-text-white tw-text-xl" />
                        </div>
                        <span className="tw-text-xs tw-text-blue-600 tw-bg-blue-50 tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium">
                            +{stats.membersAddedThisMonth} This Month
                        </span>
                    </div>
                    <p className="tw-text-2xl tw-font-bold tw-text-gray-800">{stats.totalMembers}</p>
                    <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Total Members</p>
                    <div className="tw-flex tw-gap-2 tw-mt-3">
                        <span className="tw-text-[10px] tw-bg-emerald-50 tw-text-emerald-600 tw-px-2 tw-py-0.5 tw-rounded">
                            <Icon icon="mdi:account-key" className="tw-inline tw-mr-0.5" />{stats.ownersCount} Owners
                        </span>
                        <span className="tw-text-[10px] tw-bg-amber-50 tw-text-amber-600 tw-px-2 tw-py-0.5 tw-rounded">
                            <Icon icon="mdi:account-clock" className="tw-inline tw-mr-0.5" />{stats.tenantsCount} Tenants
                        </span>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="tw-grid tw-grid-cols-4 tw-gap-3 tw-mb-5">
                {quickActions.map((action, index) => (
                    <Link
                        key={index}
                        to={action.link}
                        className={`tw-bg-gradient-to-r ${action.gradient} tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-3 tw-text-white tw-transition-all hover:tw-shadow-lg hover:tw-scale-[1.02] tw-no-underline`}
                    >
                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-white/20 tw-flex tw-items-center tw-justify-center">
                            <Icon icon={action.icon} className="tw-text-xl" />
                        </div>
                        <div>
                            <p className="tw-font-medium tw-text-sm">{action.title}</p>
                            <p className="tw-text-[10px] tw-text-white/70">Click to proceed</p>
                        </div>
                        <Icon icon="mdi:chevron-right" className="tw-ml-auto tw-text-white/50" />
                    </Link>
                ))}
            </div>

            {/* Alert Cards Row */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-5">
                <div className="tw-bg-gradient-to-r tw-from-red-50 tw-to-orange-50 tw-border tw-border-red-100 tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-red-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Icon icon="mdi:message-alert-outline" className="tw-text-white tw-text-2xl" />
                    </div>
                    <div className="tw-flex-1">
                        <p className="tw-text-xl tw-font-bold tw-text-red-700">{stats.pendingComplaints}</p>
                        <p className="tw-text-xs tw-text-red-600">Pending Complaints</p>
                    </div>
                    <Link to={SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS} className="tw-text-red-600 hover:tw-text-red-700">
                        <Icon icon="mdi:arrow-right-circle" className="tw-text-xl" />
                    </Link>
                </div>


                <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-border tw-border-blue-100 tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Icon icon="mdi:bell-badge-outline" className="tw-text-white tw-text-2xl" />
                    </div>
                    <div className="tw-flex-1">
                        <p className="tw-text-xl tw-font-bold tw-text-blue-700">{stats.newNotices}</p>
                        <p className="tw-text-xs tw-text-blue-600">New Notices</p>
                    </div>
                    <Link to={SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES} className="tw-text-blue-600 hover:tw-text-blue-700">
                        <Icon icon="mdi:arrow-right-circle" className="tw-text-xl" />
                    </Link>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="tw-grid tw-grid-cols-1 tw-gap-5">
                {/* Recent Transactions */}
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <div className="tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-gray-50 tw-to-gray-100 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:receipt-text-outline" className="tw-text-white" />
                            </div>
                            <div>
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Recent Transactions</h3>
                                <p className="tw-text-[10px] tw-text-gray-500">Latest payment activities</p>
                            </div>
                        </div>
                        <Link to={SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES} className="tw-text-xs tw-text-blue-600 tw-font-medium tw-flex tw-items-center tw-gap-1 hover:tw-text-blue-700 tw-no-underline">
                            View All <Icon icon="mdi:chevron-right" />
                        </Link>
                    </div>
                    <div className="tw-divide-y tw-divide-gray-50">
                        {recentTransactions.length === 0 && (
                            <div className="tw-px-4 tw-py-6 tw-text-center tw-text-xs tw-text-gray-400">
                                {loading ? 'Loading…' : 'No transactions yet'}
                            </div>
                        )}
                        {recentTransactions.map((txn) => (
                            <div key={txn.id} className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-50 tw-transition-colors">
                                <div className="tw-flex tw-items-center tw-gap-3">
                                    <div className={`tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center ${txn.status === 'Paid' ? 'tw-bg-emerald-100' : 'tw-bg-amber-100'}`}>
                                        <Icon icon={txn.status === 'Paid' ? 'mdi:check-circle' : 'mdi:clock-outline'} className={txn.status === 'Paid' ? 'tw-text-emerald-600' : 'tw-text-amber-600'} />
                                    </div>
                                    <div>
                                        <p className="tw-font-medium tw-text-gray-800 tw-text-sm">{txn.member}</p>
                                        <p className="tw-text-[11px] tw-text-gray-500">
                                            {txn.unit} • {txn.type}
                                            {txn.paymentMode ? ` • ${txn.paymentMode}` : ''}
                                        </p>
                                        {txn.receiptNumber ? (
                                            <p className="tw-text-[10px] tw-text-gray-400 tw-font-mono">{txn.receiptNumber}</p>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="tw-text-right">
                                    <p className={`tw-font-semibold tw-text-sm ${txn.status === 'Paid' ? 'tw-text-emerald-600' : 'tw-text-amber-600'}`}>
                                        {formatCurrency(txn.amount)}
                                    </p>
                                    <p className="tw-text-[10px] tw-text-gray-400">{formatDate(txn.date)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* Notices Section */}
            <div className="tw-mt-5 tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                <div className="tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-gray-50 tw-to-gray-100 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-indigo-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:bullhorn-outline" className="tw-text-white" />
                        </div>
                        <div>
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Recent Notices</h3>
                            <p className="tw-text-[10px] tw-text-gray-500">Important announcements</p>
                        </div>
                    </div>
                    <Link to={SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES} className="tw-text-xs tw-text-blue-600 tw-font-medium tw-flex tw-items-center tw-gap-1 hover:tw-text-blue-700 tw-no-underline">
                        View All <Icon icon="mdi:chevron-right" />
                    </Link>
                </div>
                <div className="tw-p-4">
                    {notices.length === 0 && (
                        <div className="tw-text-center tw-py-8">
                            <Icon icon="mdi:bullhorn-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No notices published yet</p>
                            <Link to={SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_ADD} className="tw-text-xs tw-text-blue-600 tw-no-underline">Create a notice</Link>
                        </div>
                    )}
                    <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                        {notices.map((notice) => (
                            <div key={notice.id} className="tw-bg-gray-50 tw-rounded-xl tw-p-4 hover:tw-shadow-md tw-transition-all tw-cursor-pointer tw-border tw-border-transparent hover:tw-border-gray-200">
                                <div className="tw-flex tw-items-start tw-justify-between tw-mb-2">
                                    <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-indigo-100 tw-flex tw-items-center tw-justify-center">
                                        <Icon icon="mdi:file-document-outline" className="tw-text-indigo-600" />
                                    </div>
                                    <span className={`tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium tw-border ${getPriorityColor(notice.priority)}`}>
                                        {notice.priority.charAt(0).toUpperCase() + notice.priority.slice(1)}
                                    </span>
                                </div>
                                <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-mb-1">{notice.title}</h4>
                                <p className="tw-text-xs tw-text-gray-500 tw-line-clamp-2 tw-mb-2">{notice.description}</p>
                                <p className="tw-text-[10px] tw-text-gray-400 tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:calendar-outline" />
                                    {formatDate(notice.date)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;
